/**
 * Fifty Rooms · /search tabs "Products · N" / "Collections · N" (03 §H6).
 * Collections come from /search/suggest (Section Rendering, section fr-search-collections, up to 10),
 * because storefront /search does not search collections. The Collections tab appears only when there is
 * at least one collection. Tabs follow the WAI-ARIA tabs pattern (roving tabindex, ←/→/Home/End).
 * The selected tab is kept in ?tab=collections (history.replaceState).
 */

const SECTION_ID = 'fr-search-collections';

class FrSearchTabs extends HTMLElement {
  /** @type {HTMLButtonElement[]} */
  tabs = [];

  /** @type {MutationObserver | undefined} */
  observer;

  connectedCallback() {
    this.tabs = /** @type {HTMLButtonElement[]} */ (Array.from(this.querySelectorAll('[role="tab"]')));
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(tab.dataset.frTab || 'products', true));
      tab.addEventListener('keydown', (event) => this.onKeydown(event));
    });
    this.loadCollections();
    this.watchProductsCount();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  /** Keep "Products · N" in sync when filters re-render the results (fr-results data-results-count). */
  watchProductsCount() {
    const panel = this.panelFor('products');
    const counter = this.tabs.find((t) => t.dataset.frTab === 'products')?.querySelector('[data-fr-tab-count]');
    if (!panel || !counter) return;
    const update = () => {
      const results = panel.querySelector('[data-results-count]');
      const value = results?.getAttribute('data-results-count');
      if (value != null && counter.textContent !== value) counter.textContent = value;
    };
    this.observer = new MutationObserver(update);
    this.observer.observe(panel, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-results-count'] });
  }

  /** @param {string} name */
  panelFor(name) {
    const tab = this.tabs.find((t) => t.dataset.frTab === name);
    const id = tab?.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  /**
   * @param {string} name - 'products' | 'collections'
   * @param {boolean} [focus]
   */
  select(name, focus = false) {
    const target = this.tabs.find((t) => t.dataset.frTab === name && !t.hidden) || this.tabs[0];
    if (!target) return;

    for (const tab of this.tabs) {
      const selected = tab === target;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      const panel = this.panelFor(tab.dataset.frTab || '');
      if (panel) panel.hidden = !selected;
    }

    if (focus) target.focus();

    try {
      const url = new URL(window.location.href);
      if (target.dataset.frTab === 'collections') {
        url.searchParams.set('tab', 'collections');
      } else {
        url.searchParams.delete('tab');
      }
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (error) {
      /* ignore */
    }
  }

  /** @param {KeyboardEvent} event */
  onKeydown(event) {
    const visible = this.tabs.filter((t) => !t.hidden);
    const index = visible.indexOf(/** @type {HTMLButtonElement} */ (event.currentTarget));
    if (index < 0) return;
    let next = -1;
    const rtl = document.documentElement.dir === 'rtl';
    if (event.key === (rtl ? 'ArrowLeft' : 'ArrowRight')) next = (index + 1) % visible.length;
    if (event.key === (rtl ? 'ArrowRight' : 'ArrowLeft')) next = (index - 1 + visible.length) % visible.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = visible.length - 1;
    if (next < 0) return;
    event.preventDefault();
    this.select(visible[next]?.dataset.frTab || 'products', true);
  }

  async loadCollections() {
    const terms = (this.dataset.terms || '').trim();
    const tab = this.tabs.find((t) => t.dataset.frTab === 'collections');
    const panel = this.panelFor('collections');
    if (!terms || !tab || !panel) return;

    const url = new URL(this.dataset.suggestUrl || '/search/suggest', window.location.origin);
    url.searchParams.set('q', terms);
    url.searchParams.set('resources[type]', 'collection');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('section_id', SECTION_ID);

    let count = 0;
    try {
      const response = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
      if (!response.ok) return;
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const list = doc.querySelector('[data-fr-search-collections]');
      count = Number(list?.getAttribute('data-count') || 0);
      if (!list || count <= 0) return;
      panel.replaceChildren(...Array.from(list.childNodes).map((node) => document.importNode(node, true)));
    } catch (error) {
      return;
    }

    const counter = tab.querySelector('[data-fr-tab-count]');
    if (counter) counter.textContent = String(count);
    tab.hidden = false;
    this.dataset.collectionsCount = String(count);

    const params = new URL(window.location.href).searchParams;
    const wanted = params.get('tab');
    const productsCount = Number(this.dataset.productsCount || 0);
    const hasFilters = Array.from(params.keys()).some((key) => key.startsWith('filter.'));
    if (wanted === 'collections' || (productsCount === 0 && !hasFilters)) {
      this.select('collections');
    }
  }
}

if (!customElements.get('fr-search-tabs')) {
  customElements.define('fr-search-tabs', FrSearchTabs);
}
